/**
 * The authenticated-portal frame: chrome bar, navigation and content column.
 * Shared by the creator, brand and staff portals so geometry and focus
 * behaviour stay identical; each portal supplies its own items.
 *
 * `variant="rail"` is the modern layout: a flush-left icon+label rail with
 * the wordmark at the top. Brand and creator keep the labelled sidebar until
 * they opt in. Below 768px the rail becomes a bottom tab bar plus a More
 * sheet. Tab items are those marked `tab: true`.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { fontSize, radius } from "@design/index";
import { Icons } from "./icons";

export interface NavItem {
  readonly label: string;
  readonly href: string;
  /** Shorter label for the mobile tab bar. */
  readonly shortLabel?: string;
  /** Marks routes that are not built yet, so the shell never lies about scope. */
  readonly pending?: boolean;
  /** Marks routes the current account is not allowed to open yet. */
  readonly locked?: boolean;
  readonly icon?: ReactNode;
  /** Section heading in the rail. Consecutive items share a group. */
  readonly group?: string;
  /** Pin this item to the mobile tab bar (plus More). */
  readonly tab?: boolean;
}

function BrandMark() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true" style={{ color: "var(--chrome-mark)" }}>
        <rect x="2" y="7.6" width="15" height="4.8" rx="2.4" fill="currentColor" />
        <rect x="2" y="13.6" width="23" height="4.8" rx="2.4" fill="currentColor" />
        <rect x="2" y="19.6" width="11" height="4.8" rx="2.4" fill="currentColor" />
        <rect x="19.9" y="4" width="2.2" height="26" rx="1.1" fill="currentColor" />
        <path d="M17.4 2.6h7.2l-1.9 2.6h-3.4z" fill="currentColor" />
      </svg>
      <span style={{ fontWeight: 700, fontSize: fontSize.bodyLg, color: "var(--chrome-fg)" }}>
        ClipMaster<span style={{ color: "var(--chrome-mark)" }}>.io</span>
      </span>
    </span>
  );
}

function unavailable(item: NavItem): "locked" | "soon" | null {
  if (item.locked) return "locked";
  if (item.pending) return "soon";
  return null;
}

function NavLinks({
  audience, nav, activeHref, rail,
}: {
  audience: string;
  nav: readonly NavItem[];
  activeHref: string;
  rail: boolean;
}) {
  let lastGroup: string | undefined;
  return (
    <nav
      aria-label={`${audience} sections`}
      className={rail ? "cm-rail-fixed" : undefined}
      style={rail ? {
        padding: "18px 12px 28px",
        display: "grid",
        gap: 4,
        alignContent: "start",
        borderRight: "1px solid var(--chrome-line)",
        background: "var(--chrome-bg)",
      } : {
        flex: "0 0 232px",
        position: "sticky",
        top: 60,
        alignSelf: "flex-start",
        padding: "20px 12px",
        display: "grid",
        gap: 2,
        alignContent: "start",
        borderRight: "1px solid var(--line)",
        minHeight: "calc(100vh - 60px)",
      }}
    >
      {rail ? (
        <a href="/" aria-label="ClipMaster.io home" style={{
          display: "flex", alignItems: "center", padding: "6px 10px 18px",
          textDecoration: "none",
        }}>
          <BrandMark />
        </a>
      ) : null}
      {nav.map((item) => {
        const note = unavailable(item);
        const active = !note && item.href === activeHref;
        const showGroup = rail && item.group && item.group !== lastGroup;
        lastGroup = item.group ?? lastGroup;
        return (
          <span key={item.href} style={{ display: "grid", gap: 4 }}>
            {showGroup ? (
              <span className="cm-rail-group">{item.group}</span>
            ) : null}
            <a
              href={item.href}
              className={rail ? "cm-rail-item" : undefined}
              title={note ? `${item.label} (${note})` : item.label}
              aria-label={note === "locked"
                ? `${item.label}, locked`
                : note === "soon" ? `${item.label}, not available yet` : item.label}
              aria-disabled={note ? true : undefined}
              aria-current={active ? "page" : undefined}
              onClick={note ? (e) => { e.preventDefault(); } : undefined}
              style={rail ? {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minHeight: 40,
                padding: "8px 10px",
                borderRadius: radius.md,
                textDecoration: "none",
                background: active ? "var(--accent)" : "transparent",
                color: active ? "var(--accent-fg)" : "var(--text-2)",
                opacity: note ? 0.55 : 1,
                cursor: note ? "not-allowed" : "pointer",
              } : {
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                padding: "8px 10px", borderRadius: radius.sm, textDecoration: "none",
                fontSize: fontSize.bodyLg, fontWeight: active ? 600 : 500,
                background: active ? "var(--chrome-active-bg)" : "transparent",
                color: active ? "var(--chrome-active-fg)" : "var(--text-2)",
                cursor: note ? "not-allowed" : "pointer",
                opacity: note ? 0.55 : 1,
              }}
            >
              {rail ? (
                <>
                  <span style={{ display: "grid", placeItems: "center", flex: "0 0 20px" }}>
                    {item.icon ?? item.label.slice(0, 1)}
                  </span>
                  <span style={{
                    fontSize: fontSize.bodyLg, fontWeight: active ? 650 : 500,
                    letterSpacing: "-0.01em",
                    flex: 1,
                  }}>
                    {item.label}
                  </span>
                  {note ? (
                    <span style={{ fontSize: fontSize.micro, color: "inherit", opacity: 0.8 }}>{note}</span>
                  ) : null}
                </>
              ) : (
                <>
                  {item.label}
                  {note ? (
                    <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>{note}</span>
                  ) : null}
                </>
              )}
            </a>
          </span>
        );
      })}
    </nav>
  );
}

function tabItemStyle(active: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    minHeight: 44,
    padding: "6px 4px",
    border: 0,
    background: "transparent",
    color: active ? "var(--accent)" : "var(--chrome-muted)",
    textDecoration: "none",
    font: "inherit",
    fontSize: 11,
    fontWeight: active ? 700 : 550,
    letterSpacing: "-0.01em",
    cursor: "pointer",
    width: "100%",
  };
}

function MobileTabBar({
  nav, activeHref, moreOpen, onMore,
}: {
  nav: readonly NavItem[];
  activeHref: string;
  moreOpen: boolean;
  onMore: () => void;
}) {
  const tabs = nav.filter((item) => item.tab);
  const tabHrefs = new Set(tabs.map((item) => item.href));
  const moreActive = moreOpen || !tabHrefs.has(activeHref);
  return (
    <nav className="cm-tabbar" aria-label="Primary">
      {tabs.map((item) => {
        const note = unavailable(item);
        const active = !moreOpen && !note && item.href === activeHref;
        return (
          <a
            key={item.href}
            href={item.href}
            className="cm-tabbar-item"
            aria-current={active ? "page" : undefined}
            aria-disabled={note ? true : undefined}
            aria-label={note ? `${item.label}, ${note}` : item.label}
            title={note ? `${item.label} (${note})` : item.label}
            onClick={note ? (e) => { e.preventDefault(); } : undefined}
            style={{
              ...tabItemStyle(active),
              opacity: note ? 0.45 : 1,
              cursor: note ? "not-allowed" : "pointer",
            }}
          >
            <span style={{ display: "grid", placeItems: "center", width: 22, height: 22 }}>
              {item.icon ?? item.label.slice(0, 1)}
            </span>
            <span style={{ display: "grid", justifyItems: "center", lineHeight: 1.05 }}>
              <span>{item.shortLabel ?? item.label}</span>
              {note ? <span style={{ fontSize: 9, letterSpacing: "0.02em" }}>{note}</span> : null}
            </span>
          </a>
        );
      })}
      <button
        type="button"
        className="cm-tabbar-item"
        aria-label="More sections"
        aria-expanded={moreOpen}
        aria-controls="cm-more-sheet"
        onClick={onMore}
        style={tabItemStyle(moreActive)}
      >
        <span style={{ display: "grid", placeItems: "center", width: 22, height: 22 }}>
          {Icons.more}
        </span>
        More
      </button>
    </nav>
  );
}

function MoreSheet({
  nav, activeHref, open, onClose,
}: {
  nav: readonly NavItem[];
  activeHref: string;
  open: boolean;
  onClose: () => void;
}) {
  const extra = nav.filter((item) => !item.tab);
  return (
    <div
      className={`cm-more${open ? " is-open" : ""}`}
      hidden={!open}
      onClick={onClose}
    >
      <div
        id="cm-more-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="More sections"
        className="cm-more-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cm-more-handle" aria-hidden="true" />
        <p className="cm-more-title">More</p>
        <ul className="cm-more-list">
          {extra.map((item) => {
            const note = unavailable(item);
            const active = !note && item.href === activeHref;
            return (
              <li key={item.href}>
                <a
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  aria-disabled={note ? true : undefined}
                  aria-label={note ? `${item.label}, ${note}` : item.label}
                  onClick={(e) => {
                    if (note) {
                      e.preventDefault();
                      return;
                    }
                    // Do not close on this tap. Hiding the sheet here lets
                    // the same touch fall through onto the tab bar, which is
                    // why Profile looked like it did nothing. The shell
                    // closes when the route changes. If we are already here,
                    // close after the event so the tap cannot miss.
                    if (item.href === activeHref) {
                      e.preventDefault();
                      window.setTimeout(onClose, 0);
                    }
                  }}
                  className="cm-more-item"
                  style={{
                    background: active ? "var(--accent-soft)" : "transparent",
                    color: active ? "var(--accent-soft-fg)" : "var(--text)",
                    opacity: note ? 0.55 : 1,
                    cursor: note ? "not-allowed" : "pointer",
                  }}
                >
                  <span style={{ display: "grid", placeItems: "center", width: 22, color: "currentColor" }}>
                    {item.icon ?? item.label.slice(0, 1)}
                  </span>
                  <span>{item.label}</span>
                  {note ? <span className="cm-more-soon">{note}</span> : null}
                </a>
              </li>
            );
          })}
        </ul>
        <button type="button" className="cm-more-close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export function PortalShell({
  audience, nav, activeHref, children, variant = "sidebar", headerEnd,
}: {
  audience: string;
  nav: readonly NavItem[];
  activeHref: string;
  children: ReactNode;
  variant?: "sidebar" | "rail";
  headerEnd?: ReactNode;
}) {
  const rail = variant === "rail";
  const [moreOpen, setMoreOpen] = useState(false);
  const hasTabs = rail && nav.some((item) => item.tab);

  useEffect(() => { setMoreOpen(false); }, [activeHref]);

  useEffect(() => {
    if (!moreOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const header = (
    <header
      data-chrome="true"
      className="cm-portal-header"
      style={{ position: "sticky", top: 0, zIndex: 30, height: 60, display: "flex",
        alignItems: "center", gap: 16, padding: rail ? "0 24px 0 20px" : "0 20px",
        background: "var(--chrome-bg)", borderBottom: "1px solid var(--chrome-line)" }}
    >
      {rail ? (
        <>
          <a href="/" className="cm-mobile-brand" aria-label="ClipMaster.io home" style={{ textDecoration: "none" }}>
            <BrandMark />
          </a>
          <span className="cm-desktop-audience" style={{ fontFamily: "var(--font-mono)", fontSize: fontSize.label,
              letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--chrome-muted)" }}>
            {audience}
          </span>
        </>
      ) : (
        <>
          <BrandMark />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: fontSize.label,
              letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--chrome-muted)" }}>
            {audience}
          </span>
        </>
      )}
      <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 12 }}>
        <span className="cm-header-profile">{headerEnd}</span>
      </span>
    </header>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text)",
                  display: rail ? "flex" : undefined, alignItems: rail ? "stretch" : undefined }}>
      <a href="#main" style={{ position: "absolute", left: 16, top: -60, zIndex: 60,
          padding: "10px 14px", background: "var(--primary-bg)", color: "var(--primary-fg)",
          borderRadius: radius.control, fontWeight: 600, textDecoration: "none" }}>
        Skip to content
      </a>
      {rail ? <NavLinks audience={audience} nav={nav} activeHref={activeHref} rail /> : header}
      <div
        className={rail ? "cm-rail-offset" : undefined}
        style={{ flex: rail ? 1 : undefined, minWidth: 0,
                  display: rail ? "flex" : undefined, flexDirection: rail ? "column" : undefined }}
      >
        {rail ? header : null}
        <div style={{ display: "flex", alignItems: "flex-start",
                      minHeight: rail ? undefined : "calc(100vh - 60px)", flex: rail ? 1 : undefined }}>
          {rail ? null : <NavLinks audience={audience} nav={nav} activeHref={activeHref} rail={false} />}
          <main
            id="main"
            className="cm-portal-main"
            style={{
              flex: 1,
              minWidth: 0,
              maxWidth: rail ? 1600 : 1088,
              margin: rail ? "0 auto" : 0,
              width: rail ? "100%" : undefined,
              padding: rail ? "28px 36px 72px" : "28px 24px 64px",
              display: "grid",
              gap: 28,
              alignContent: "start",
            }}
          >
            {children}
          </main>
        </div>
      </div>
      {hasTabs ? (
        <>
          <MobileTabBar
            nav={nav}
            activeHref={activeHref}
            moreOpen={moreOpen}
            onMore={() => setMoreOpen((v) => !v)}
          />
          <MoreSheet
            nav={nav}
            activeHref={activeHref}
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
          />
        </>
      ) : null}
    </div>
  );
}
